import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { SearchReactElement, SelectOption } from '../types'

import { useLocalFilterOptions } from './useLocalFilterOptions'

export type UseSelectSearchParams = {
  /** Options disponibles */
  options: SelectOption[]
  /** Composant de recherche extrait du composant Select */
  searchComponent?: SearchReactElement
}

export type UseSelectSearchReturn = {
  /** Valeur de recherche actuelle */
  searchValue: string
  /** Callback pour changer la valeur de recherche */
  handleSearchChange: (value: string) => void
  /** Réinitialise la recherche */
  resetSearch: () => void
  /** Options filtrées selon la recherche */
  filteredOptions: SelectOption[]
  /** Indique si la recherche est activée */
  hasSearch: boolean
  /** Indique si c'est une recherche locale */
  isLocalSearch: boolean
  /** Placeholder du champ de recherche */
  searchPlaceholder: string
  /** État de chargement de la recherche remote */
  loading: boolean
}

/**
 * Hook pour gérer la logique de recherche du Select
 *
 * Gère :
 * - L'extraction des blocs de recherche (SelectSearch ou SelectLocalSearch)
 * - La valeur de recherche
 * - Le filtrage local des options
 * - Les callbacks de recherche remote
 */
export const useSelectSearch = ({
  options,
  searchComponent,
}: UseSelectSearchParams): UseSelectSearchReturn => {
  const [searchValue, setSearchValue] = useState('')

  const { t } = useTranslation()

  // Configuration de la recherche
  const hasSearch = !!searchComponent
  // @ts-expect-error - Only LocalSearch has searchBy and searchFilter props
  const onRemoteSearch = searchComponent?.props?.onSearch
  const isLocalSearch = onRemoteSearch === undefined && hasSearch
  const searchPlaceholder = searchComponent?.props?.placeholder || t('common.search')
  // @ts-expect-error - Only RemoteSearch
  const isFetchingRemoteData = searchComponent?.props?.isFetching

  // Filtrage local des options si recherche locale activée
  const filterOptionsLocally = useLocalFilterOptions({
    searchValue,
    // @ts-expect-error - Only LocalSearch
    searchBy: searchComponent?.props?.searchBy,
    // @ts-expect-error - Only LocalSearch
    searchFilter: searchComponent?.props?.searchFilter,
  })

  const filteredOptions = useMemo(() => {
    if (!hasSearch) {
      return options
    }

    if (isLocalSearch) {
      return filterOptionsLocally(options)
    }

    // Remote
    return options
  }, [hasSearch, isLocalSearch, options, filterOptionsLocally])

  // Gestion du changement de valeur de recherche
  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    onRemoteSearch?.(value)
  }

  // Réinitialise la recherche
  const resetSearch = () => {
    setSearchValue('')
    onRemoteSearch?.('')
  }

  return {
    searchValue,
    handleSearchChange,
    resetSearch,
    filteredOptions,
    hasSearch,
    isLocalSearch,
    searchPlaceholder,
    loading: isFetchingRemoteData,
  }
}
